import React, {createContext, useContext, useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {useLoyalty} from '../integrations/loyalty/use-loyalty'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../utils/site-config'

/**
 * LoyaltyDataContext
 *
 * Fetches Salesforce Loyalty Management `sync-data` once per registered
 * customer session and shares the resulting payload with any descendant
 * component (e.g. GDIRewardTileBlock instances inside the rewards page CMS
 * tree) so they don't each have to issue their own request.
 *
 * Consumers should treat `loyaltyData` as `null` until `isReady` flips true.
 */

const LoyaltyDataContext = createContext({
    loyaltyData: null,
    isLoading: false,
    isReady: false,
    isRegistered: false
})

export const LoyaltyDataProvider = ({children}) => {
    const {app: appConfig} = getConfig()
    const {site} = useMultiSite()
    const sflmEnabled = getSiteEnvVarValue(appConfig, 'SFLM_ENABLED', site?.id) === 'true'
    const {data: customer} = useCurrentCustomer()
    const {syncLoyaltyData} = useLoyalty()
    const [loyaltyData, setLoyaltyData] = useState(null)
    const [isLoading, setIsLoading] = useState(false)
    const [isReady, setIsReady] = useState(false)

    useEffect(() => {
        let cancelled = false
        if (!sflmEnabled) {
            setIsReady(true)
            return undefined
        }
        if (!customer?.isRegistered) {
            // For anonymous / guest visitors there's nothing to sync; mark
            // ready so consumers can stop waiting and render the empty state.
            setLoyaltyData(null)
            setIsReady(true)
            return undefined
        }
        setIsLoading(true)
        setIsReady(false)
        ;(async () => {
            try {
                const data = await syncLoyaltyData()
                if (!cancelled) setLoyaltyData(data)
            } finally {
                if (!cancelled) {
                    setIsLoading(false)
                    setIsReady(true)
                }
            }
        })()
        return () => {
            cancelled = true
        }
    }, [sflmEnabled, customer?.isRegistered])

    return (
        <LoyaltyDataContext.Provider
            value={{
                loyaltyData,
                isLoading,
                isReady,
                isRegistered: !!customer?.isRegistered
            }}
        >
            {children}
        </LoyaltyDataContext.Provider>
    )
}

LoyaltyDataProvider.propTypes = {
    children: PropTypes.node
}

export const useLoyaltyData = () => useContext(LoyaltyDataContext)
